import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { getUserShareLinks } from '../../services/api'
import { getCurrentUserUuid } from '../../services/userManager'

interface ShareLink {
  shareId: string
  projectUuid: string
  createdAt: string
  expiresAt: string | null
  allowDownload?: boolean
  message?: string | null
  hasPassword?: boolean
  lockInterface?: boolean
  shareUrl: string
}

interface ShareLinkSelectorDialogProps {
  onClose: () => void
  onSelect: (shareUrl: string) => void
  currentLink?: string // 当前已设置的链接
}

const ShareLinkSelectorDialog = ({ onClose, onSelect, currentLink }: ShareLinkSelectorDialogProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([])
  const [error, setError] = useState<string | null>(null)
  const [customLink, setCustomLink] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)

  useEffect(() => {
    loadShareLinks()
  }, [])

  const loadShareLinks = async () => {
    const userUuid = await getCurrentUserUuid()
    if (!userUuid) {
      setError('本地没有用户UUID，请先访问主页面创建用户')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const response = await getUserShareLinks(userUuid)
      if (response.success) {
        setShareLinks(response.data || [])
      } else {
        setError(response.message || '加载分享链接失败')
      }
    } catch (err: any) {
      setError(err.message || '加载分享链接失败')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectLink = (shareUrl: string) => {
    onSelect(shareUrl)
    onClose()
  }

  const handleCustomLink = () => {
    if (customLink.trim()) {
      onSelect(customLink.trim())
      onClose()
    }
  }

  const handleClearLink = () => {
    onSelect('')
    onClose()
  }

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            选择分享链接
          </h3>
          <p className="text-sm text-gray-500">
            选择您要链接到的画布，或输入自定义链接
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">加载中...</div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => window.open('/', '_blank')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                前往主页面
              </button>
            </div>
          ) : (
            <>
              {/* 清除链接按钮 */}
              {currentLink && (
                <button
                  onClick={handleClearLink}
                  className="w-full mb-4 px-4 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                >
                  清除当前链接
                </button>
              )}

              {/* 分享链接列表 */}
              {shareLinks.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {shareLinks.map((link) => {
                    const isSelected = currentLink === link.shareUrl
                    return (
                      <button
                        key={link.shareId}
                        onClick={() => handleSelectLink(link.shareUrl)}
                        className={`w-full text-left px-4 py-3 rounded-md border transition-colors ${
                          isSelected
                            ? 'bg-blue-50 border-blue-300 text-blue-900'
                            : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-900'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 pr-2">
                            <div className="text-sm font-medium truncate flex items-center gap-2">
                              {link.shareUrl}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigator.clipboard.writeText(link.shareUrl)
                                  // 这里可以添加复制成功的提示，但为了简单起见，可以暂时不加
                                }}
                                className="p-1 hover:bg-blue-100 rounded-full text-gray-400 hover:text-blue-600 transition-colors"
                                title="复制链接"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                              </button>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              创建于: {new Date(link.createdAt).toLocaleString('zh-CN')}
                            </div>
                          </div>
                          {isSelected && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600 ml-2 flex-shrink-0">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-4">您还没有创建任何分享链接</p>
                  <button
                    onClick={() => window.open('/', '_blank')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    前往主页面创建
                  </button>
                </div>
              )}

              {/* 自定义链接输入 */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => setShowCustomInput(!showCustomInput)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors flex items-center justify-between"
                >
                  <span>输入自定义链接</span>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    className={`transition-transform ${showCustomInput ? 'rotate-180' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                {showCustomInput && (
                  <div className="mt-3">
                    <input
                      type="text"
                      value={customLink}
                      onChange={(e) => setCustomLink(e.target.value)}
                      placeholder="例如: /share/abc123 或完整URL"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCustomLink()
                        } else if (e.key === 'Escape') {
                          setShowCustomInput(false)
                        }
                      }}
                      autoFocus
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setShowCustomInput(false)}
                        className="flex-1 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                      >
                        取消
                      </button>
                      <button
                        onClick={handleCustomLink}
                        className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        确定
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            取消
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ShareLinkSelectorDialog
